/**
 * The truck. A Ville de Montréal works truck, which is to say an old inline-six diesel with
 * a tired turbo and a body full of loose tools.
 *
 * WHY IT SOUNDS LIKE A TRUCK AND NOT A CAR:
 *   · The fundamental is the FIRING frequency, rpm/20 for a six-cylinder four-stroke. A
 *     620 rpm idle is 31 Hz and the 4200 rpm ceiling is 210 Hz — the entire range lives
 *     below 250 Hz, which is most of what "heavy" means.
 *   · A square oscillator at HALF the firing rate sits under everything. That sub-order
 *     component is the diesel lug: the uneven, loping beat you hear from a truck at idle.
 *   · The whole engine is additionally amplitude-modulated at crank rate (firing/3), deep at
 *     idle and almost gone at full rev. That is the audible chug.
 *   · The master lowpass opens with rpm, so revving brightens rather than merely rising.
 *
 * All nodes here are created once, in build(), and their AudioParams are automated. Nothing
 * is created per frame.
 */

import { AUDIO_TUNING as T } from './tuning.js';

const E = T.engine;
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

export class EngineVoice {
  constructor(graph) {
    this.graph = graph;
    this.built = false;

    // Simulation-facing state. Updated even when there is no audio, so harnessStats() is
    // meaningful in the headless harness.
    this.rpm = E.idleRpm;
    this.load = 0;
    this.speed = 0;
    this.targetRpm = E.idleRpm;
    this.targetLoad = 0;
    this.f0 = E.f0Min;
    this.enabled = true;
  }

  build() {
    const g = this.graph;
    if (!g.live || this.built) return;
    const a = g.actx;
    const now = a.currentTime;

    // ---- output chain -------------------------------------------------
    this.out = a.createGain();
    this.out.gain.value = 0.0001;
    this.out.connect(g.bus.engine);

    this.lpf = a.createBiquadFilter();
    this.lpf.type = 'lowpass';
    this.lpf.frequency.value = E.lpf.base;
    this.lpf.Q.value = E.lpf.q;
    this.lpf.connect(this.out);

    // Sum node the oscillators feed. Kept separate from `out` so the lug LFO can modulate
    // `out.gain` without fighting the level automation.
    this.sum = a.createGain();
    this.sum.gain.value = 1;
    this.sum.connect(this.lpf);

    // ---- oscillator stack ---------------------------------------------
    this.osc = [];
    for (const spec of [E.order1, E.order2, E.order3, E.sub]) {
      const o = a.createOscillator();
      o.type = spec.type;
      o.frequency.value = E.f0Min * spec.mul;
      o.detune.value = spec.detune;
      const og = a.createGain();
      og.gain.value = spec.gain;
      o.connect(og); og.connect(this.sum);
      o.start(now);
      this.osc.push({ o, g: og, mul: spec.mul });
    }

    // ---- combustion roar ------------------------------------------------
    this.roarSrc = g.makeNoiseSource('white');
    this.roarBp = a.createBiquadFilter();
    this.roarBp.type = 'bandpass';
    this.roarBp.frequency.value = E.roar.bandMin;
    this.roarBp.Q.value = E.roar.q;
    this.roarGain = a.createGain();
    this.roarGain.gain.value = E.roar.gainIdle;
    this.roarSrc.connect(this.roarBp); this.roarBp.connect(this.roarGain); this.roarGain.connect(this.sum);

    // ---- off-throttle rumble -------------------------------------------
    this.rumbleSrc = g.makeNoiseSource('pink');
    this.rumbleSrc.playbackRate.value = 0.87;      // de-correlate from the roar layer
    this.rumbleLp = a.createBiquadFilter();
    this.rumbleLp.type = 'lowpass';
    this.rumbleLp.frequency.value = E.offThrottle.lpHz;
    this.rumbleLp.Q.value = E.offThrottle.q;
    this.rumbleGain = a.createGain();
    this.rumbleGain.gain.value = 0.0001;
    this.rumbleSrc.connect(this.rumbleLp); this.rumbleLp.connect(this.rumbleGain); this.rumbleGain.connect(this.sum);

    // ---- turbo whine ----------------------------------------------------
    this.turboA = a.createOscillator(); this.turboA.type = 'triangle';
    this.turboB = a.createOscillator(); this.turboB.type = 'sine';
    this.turboB.detune.value = E.turbo.detune;
    this.turboGain = a.createGain(); this.turboGain.gain.value = 0.0001;
    this.turboA.frequency.value = E.turbo.hzAtIdle;
    this.turboB.frequency.value = E.turbo.hzAtIdle * 1.5;
    this.turboA.connect(this.turboGain);
    this.turboB.connect(this.turboGain);
    // Turbo sits OUTSIDE the engine lowpass, otherwise the filter that makes the truck
    // heavy would also erase the only bright thing in the sound.
    this.turboGain.connect(this.out);
    this.turboA.start(now); this.turboB.start(now);

    // ---- the lug --------------------------------------------------------
    this.lugOsc = a.createOscillator();
    this.lugOsc.type = 'sine';
    this.lugOsc.frequency.value = E.lug.minHz;
    this.lugDepth = a.createGain();
    this.lugDepth.gain.value = E.lug.depthIdle;
    this.lugOsc.connect(this.lugDepth);
    this.lugDepth.connect(this.out.gain);   // AM on the engine output
    this.lugOsc.start(now);

    this.built = true;
    this._apply(0.05);
  }

  /** Called from vehicle.fixedUpdate via ctx.audio.setEngine. Sim-safe: stores only. */
  set(rpm, load, speed) {
    this.targetRpm = rpm;
    this.targetLoad = load;
    this.speed = speed;
  }

  /**
   * Presentation step. `dt` is the frame delta (or SIM_DT when clocks are frozen), so the
   * smoothing is a pure function of the step sequence in the harness.
   */
  update(dt, airborne) {
    // Smoothing runs whether or not audio is live, so harnessStats is always meaningful.
    const kr = 1 - Math.exp(-E.rpmSmooth * dt);
    const kl = 1 - Math.exp(-E.loadSmooth * dt);
    this.rpm += (this.targetRpm - this.rpm) * kr;
    const wantLoad = airborne ? E.airborneLoad : this.targetLoad;
    this.load += (wantLoad - this.load) * kl;

    const rpm01 = clamp01((this.rpm - E.idleRpm) / (E.maxRpm - E.idleRpm));
    let f0 = this.rpm / E.rpmPerHz;
    if (f0 < E.f0Min) f0 = E.f0Min;
    if (f0 > E.f0Max) f0 = E.f0Max;
    this.f0 = f0;
    this.rpm01 = rpm01;

    if (this.graph.live && this.built && this.enabled) this._apply(E.paramGlide);
  }

  _apply(glide) {
    const g = this.graph;
    const rpm01 = this.rpm01 || 0;
    const load = this.load;
    const f0 = this.f0;

    for (let i = 0; i < this.osc.length; i++) {
      g.param(this.osc[i].o.frequency, f0 * this.osc[i].mul, glide);
    }

    // Roar: band follows the 4th order but is clamped, gain follows load.
    const roarHz = Math.min(E.roar.bandMax, Math.max(E.roar.bandMin, f0 * E.roar.bandMul));
    g.param(this.roarBp.frequency, roarHz, glide);
    g.param(this.roarGain.gain, E.roar.gainIdle + E.roar.gainLoad * load, glide);

    // Off-throttle rumble: loud when you are moving fast with your foot off it.
    const speed01 = clamp01(this.speed / 20);
    const offAmount = clamp01((1 - load) * 1.2) * speed01;
    g.param(this.rumbleGain.gain, 0.0001 + E.offThrottle.gain * offAmount, glide);

    // Turbo whine — only past the threshold, and it squares up so it arrives suddenly.
    const w = clamp01((rpm01 - E.turbo.whineFrom) / (1 - E.turbo.whineFrom));
    const turboHz = E.turbo.hzAtIdle + (E.turbo.hzAtMax - E.turbo.hzAtIdle) * rpm01;
    g.param(this.turboA.frequency, turboHz, glide);
    g.param(this.turboB.frequency, turboHz * 1.5, glide);
    g.param(this.turboGain.gain, 0.0001 + E.turbo.gain * w * w * (0.35 + 0.65 * load), glide);

    // Lug: crank-rate AM, deep at idle, gone at speed.
    const lugHz = Math.min(E.lug.maxHz, Math.max(E.lug.minHz, f0 / E.lug.rateDiv));
    const lugDepth = E.lug.depthIdle + (E.lug.depthMax - E.lug.depthIdle) * rpm01;
    g.param(this.lugOsc.frequency, lugHz, glide);
    g.param(this.lugDepth.gain, lugDepth, glide);

    // Filter opens with rpm.
    g.param(this.lpf.frequency, E.lpf.base + E.lpf.perRpm01 * rpm01, glide);

    // Level.
    const lvl = E.gainIdle + (E.gainMax - E.gainIdle) * (0.45 * rpm01 + 0.55 * load);
    g.param(this.out.gain, lvl, glide);
  }

  setEnabled(on) {
    this.enabled = on;
    if (this.graph.live && this.built) this.graph.param(this.out.gain, on ? 0.3 : 0.0001, 0.05);
  }

  reset() {
    this.rpm = E.idleRpm; this.targetRpm = E.idleRpm;
    this.load = 0; this.targetLoad = 0; this.speed = 0;
    this.f0 = E.f0Min; this.rpm01 = 0;
    if (this.graph.live && this.built) this._apply(0.02);
  }

  stats() {
    return {
      rpm: Math.round(this.rpm),
      load: +this.load.toFixed(3),
      f0: +this.f0.toFixed(2),
      built: this.built,
    };
  }
}
